import LinkifyIt from "linkify-it";
import {EGiftCard} from "../Helpers/Types";
import {roundToDecimals} from "../Helpers/Functions";

// These mirror EGiftCardValidator in the bLoyal package (Common/Model); the Loyalty Engine applies the same rules to
// every setegiftcards request, so keep the two in step.
export const MaxNameLength = 50;
export const MaxEmailLength = 100;
export const MaxMessageLength = 1000;

// Control characters other than tab and newline, which are handled separately.
// eslint-disable-next-line no-control-regex
const controlCharacters = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g;

// Control characters allowed nowhere in a name (tab is turned into a space), and in the message other than tab and
// line breaks. Names can end up in email headers, where a line break could add a header.
// eslint-disable-next-line no-control-regex
const nameControlCharacters = /[\u0000-\u0008\u000A-\u001F\u007F-\u009F]/;
// eslint-disable-next-line no-control-regex
const messageControlCharacters = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/;

// Bidi overrides and isolates, which can reverse how text displays to disguise a name or link, and zero-width
// characters, which can hide text. The message keeps the zero-width joiners (U+200C, U+200D) for emoji and scripts
// that need them.
const nameHiddenCharacters = /[‪-‮⁦-⁩​-‍﻿]/;
const messageHiddenCharacters = /[‪-‮⁦-⁩​﻿]/;

// Text a mail app would turn into a link. linkify-it finds links, "www." and email addresses the way mail apps do; the
// extra top-level domains match the server's list in EGiftCardValidator.
const linkify = new LinkifyIt({fuzzyIP: true}).tlds(
    ['app', 'dev', 'xyz', 'top', 'online', 'site', 'store', 'club', 'live', 'link', 'click', 'gift', 'gifts', 'card',
        'cards', 'email', 'support', 'help'], true);
const scriptSchemes = /\b(?:javascript|data|vbscript):/i;

export const roundToCents = (value: number): number => roundToDecimals(value, 2);

export const containsHtmlCharacters = (value?: string | null): boolean => /[<>]/.test((value ?? '').normalize('NFKC'));

export const containsLinks = (value?: string | null): boolean => {
    const text = (value ?? '').normalize('NFKC');
    return linkify.test(text) || scriptSchemes.test(text);
};

const standardizeName = (value: string): string =>
    (value ?? '').normalize('NFKC').replace(controlCharacters, '').replace(/\s+/g, ' ').trim();

const standardizeMessage = (value: string): string =>
    (value ?? '').normalize('NFKC').replace(/\r\n?/g, '\n').replace(/\t/g, ' ').replace(controlCharacters, '').trim();

const standardizeEmail = (value: string): string => (value ?? '').trim().toLowerCase();

export const standardizeEGiftCard = (card: EGiftCard): EGiftCard => ({
    ...card,
    CustomAmount: card.CustomAmount === undefined || isNaN(card.CustomAmount) ? card.CustomAmount : roundToCents(card.CustomAmount),
    RecipientName: standardizeName(card.RecipientName),
    SenderName: standardizeName(card.SenderName),
    RecipientEmail: standardizeEmail(card.RecipientEmail),
    SenderEmail: standardizeEmail(card.SenderEmail),
    Message: standardizeMessage(card.Message),
});

const textFields = [
    {id: 'RecipientName', label: 'Recipient name', multiline: false},
    {id: 'SenderName', label: 'Sender name', multiline: false},
    {id: 'Message', label: 'Message', multiline: true},
] as const;

export type TextFieldError = { id: string, message: string };

// The problems the server would reject in a gift card's names and message, one per broken rule.
export const getTextFieldErrors = (card: EGiftCard): Array<TextFieldError> => {
    const errors: Array<TextFieldError> = [];
    for (const field of textFields) {
        const value = (card[field.id] ?? '').normalize('NFKC');
        if (!value)
            continue;

        if (containsHtmlCharacters(value))
            errors.push({id: field.id, message: `${field.label} cannot contain < or >.`});
        if ((field.multiline ? messageControlCharacters : nameControlCharacters).test(value))
            errors.push({id: field.id, message: `${field.label} cannot contain ${field.multiline ? '' : 'line breaks or '}control characters.`});
        if ((field.multiline ? messageHiddenCharacters : nameHiddenCharacters).test(value))
            errors.push({id: field.id, message: `${field.label} cannot contain hidden or text-direction characters.`});
        if (containsLinks(value))
            errors.push({id: field.id, message: `${field.label} cannot contain web addresses or email addresses.`});
    }

    return errors;
};

// Ids of the inputs on a gift card that the server would reject.
export const getInvalidTextFieldIds = (card: EGiftCard): Array<string> =>
    Array.from(new Set(getTextFieldErrors(card).map(e => e.id)));
